import { z } from 'zod';

const BANGLADESH_TIME_ZONE = 'Asia/Dhaka';

const formatBangladeshDate = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BANGLADESH_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const value = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value;

    return `${value('year')}-${value('month')}-${value('day')}`;
};

const parseExpirationDate = (value: unknown) => {
    if (!(value instanceof Date) && typeof value !== 'string') {
        return null;
    }

    const expirationDate = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(expirationDate.getTime())) {
        return null;
    }

    return expirationDate;
};

export const isPurchaseExpirationDateToday = (value: unknown) => {
    const expirationDate = parseExpirationDate(value);

    return (
        expirationDate !== null &&
        formatBangladeshDate(expirationDate) ===
            formatBangladeshDate(new Date())
    );
};

export const hasValidPurchaseExpirationDate = (value: unknown) => {
    const expirationDate = parseExpirationDate(value);

    if (!expirationDate) {
        return false;
    }

    return (
        formatBangladeshDate(expirationDate) >= formatBangladeshDate(new Date())
    );
};

export const PurchaseItemSchema = z.object({
    product_variation_id: z.string().min(1),
    product_name: z.string().min(1),
    quantity: z.coerce.number().int().min(1),
    purchase_price: z.coerce.number().min(0),
    wholesale_price: z.coerce.number().min(0),
    retail_price: z.coerce.number().min(0),
    expiration_date: z
        .union([z.string(), z.date()])
        .refine(
            hasValidPurchaseExpirationDate,
            'Expiration date must be today or later',
        ),
});

export const PurchaseFormSchema = z.object({
    note: z.string().optional().nullable(),
    supplier_id: z.string().min(1, 'Supplier is required'),
    supplier_name: z.string().optional().nullable(),
    items: z.array(PurchaseItemSchema).min(1, 'Add at least one item'),
    total_paid_amount: z.coerce.number().min(0),
});

export type TPurchaseForm = z.infer<typeof PurchaseFormSchema>;
export type TPurchaseItem = z.infer<typeof PurchaseItemSchema>;

export const calculatePurchaseTotal = (
    items: Array<Pick<TPurchaseItem, 'purchase_price' | 'quantity'>>,
) =>
    Math.round(
        items.reduce(
            (total, item) => total + item.purchase_price * item.quantity,
            0,
        ),
    );

export const mapPurchasePayload = (
    form: TPurchaseForm,
    prePurchaseId?: string,
) => ({
    supplier_id: form.supplier_id,
    note: form.note,
    discount: 0,
    total_price: calculatePurchaseTotal(form.items),
    total_paid: Math.round(form.total_paid_amount),
    products: form.items.map((item) => ({
        product_variation_id: item.product_variation_id,
        quantity: item.quantity,
        purchase_price: item.purchase_price,
        wholesale_price: item.wholesale_price,
        retail_price: item.retail_price,
        expiration_date: item.expiration_date,
    })),
    ...(prePurchaseId ? { pre_purchase_id: prePurchaseId } : {}),
});
